import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  ColorInput,
  Fieldset,
  FileInput,
  Group,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { WHEEL_COLOR_DEFAULTS } from "@mip-tg-bot-turntable/platform-api";
import { adminErrText, adminGet, adminSend, adminUpload, AdminApiError } from "../../api.ts";
import { textKeyZh } from "../../lib/textKeys.ts";

interface BotDetail {
  id: string;
  wheelConfig: Record<string, unknown>;
  defaultLanguage: string;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");

export function WheelTab(props: { readonly botId: string }) {
  const { botId } = props;
  const qc = useQueryClient();
  const form = useForm<Record<string, unknown>>({ initialValues: {} });
  const [uploading, setUploading] = useState<string | null>(null);

  const bot = useQuery({
    queryKey: ["bot", botId, "detail"],
    queryFn: () => adminGet<BotDetail>(`/bots/${botId}`),
  });
  // bot_text 的全部 key，供中心按钮文案 key 下拉选择。
  const textsQuery = useQuery({
    queryKey: ["bot", botId, "texts"],
    queryFn: () => adminGet<{ items: Array<{ key: string }> }>(`/bots/${botId}/texts`),
  });
  const textKeys = Array.from(new Set((textsQuery.data?.items ?? []).map((t) => t.key))).sort();
  // 下拉选项：中文描述 + key 作 label，value 取 key。并入当前值，
  // 防止历史 key 不在 bot_text 列表里时显示空白。
  const keyOptions = (current: string): Array<{ value: string; label: string }> => {
    const set = new Set(textKeys);
    if (current !== "") set.add(current);
    return Array.from(set)
      .sort()
      .map((k) => ({ value: k, label: `${textKeyZh(k)}（${k}）` }));
  };
  const patch = useMutation({
    mutationFn: (wheelConfig: Record<string, unknown>) =>
      adminSend("PATCH", `/bots/${botId}`, { wheelConfig }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已保存，已通知 Bot 热加载" });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "detail"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  // 仅首次加载回填表单，避免后台 refetch（窗口聚焦 / 同 query key 被其它 Tab 失效）
  // 覆盖用户正在编辑但未保存的转盘配置。
  const filledRef = useRef(false);
  useEffect(() => {
    if (bot.data && !filledRef.current) {
      filledRef.current = true;
      // 颜色默认值垫底：存量 bot 的 wheel_config 缺新增颜色键时，回填默认值而非空白。
      form.setValues({ ...WHEEL_COLOR_DEFAULTS, ...bot.data.wheelConfig });
    }
  }, [bot.data]);

  const handleSubmit = (v: Record<string, unknown>) => {
    patch.mutate({ ...bot.data?.wheelConfig, ...v });
  };

  const sectorColors: string[] = Array.isArray(form.values["sector_colors"])
    ? (form.values["sector_colors"] as string[])
    : [];

  // 资源字段：URL 文本框（空存 null）+ 文件上传。上传成功把返回 url 写回该字段。
  const handleAssetUpload = async (key: string, file: File | null): Promise<void> => {
    if (file === null) return;
    setUploading(key);
    try {
      const r = await adminUpload(file);
      form.setFieldValue(key, r.url);
    } catch (e) {
      notifications.show({
        color: "red",
        message: adminErrText((e as AdminApiError).code, "上传失败"),
      });
    } finally {
      setUploading(null);
    }
  };
  const uploadField = (key: string, label: string, accept: "image/*" | "audio/*") => (
    <Stack gap={4}>
      <TextInput
        label={label}
        placeholder="https://... 或用下方上传"
        value={str(form.values[key])}
        onChange={(e) => form.setFieldValue(key, e.currentTarget.value.trim() || null)}
      />
      <FileInput
        placeholder={accept === "audio/*" ? "上传音频文件" : "上传图片文件"}
        accept={accept}
        clearable
        disabled={uploading === key}
        onChange={(file) => void handleAssetUpload(key, file)}
      />
    </Stack>
  );

  // 固定色字段：值始终为字符串。format=rgba 支持透明度（如中心文字禁用态）。
  const colorField = (key: string, label: string) => (
    <ColorInput
      label={label}
      format="rgba"
      value={str(form.values[key])}
      onChange={(v) => form.setFieldValue(key, v)}
    />
  );

  if (bot.isLoading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Text fw={600} mb="md">
          转盘动效配置
        </Text>
        <Stack gap="sm">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} h={56} radius="sm" />
          ))}
        </Stack>
      </Card>
    );
  }

  if (bot.isError) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Alert color="red" title="加载失败">
          转盘配置加载失败，请刷新页面重试。
        </Alert>
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Text fw={600} mb="md">
        转盘动效配置
      </Text>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Fieldset legend="背景">
            <Stack gap="sm">
              <ColorInput
                label="背景颜色"
                format="rgba"
                value={str(form.values["background_color"])}
                onChange={(v) => form.setFieldValue("background_color", v)}
              />
              {uploadField("background_image_url", "背景图（可选）", "image/*")}
            </Stack>
          </Fieldset>

          <Fieldset legend="中心按钮文案">
            <SimpleGrid cols={2}>
              <Select
                label="可抽时文案 i18n key"
                placeholder="选择文案 key"
                data={keyOptions(str(form.values["center_label_key"]))}
                searchable
                clearable
                value={str(form.values["center_label_key"]) || null}
                onChange={(v) => form.setFieldValue("center_label_key", v ?? "")}
              />
              <Select
                label="无次数时文案 i18n key"
                placeholder="选择文案 key"
                data={keyOptions(str(form.values["center_disabled_label_key"]))}
                searchable
                clearable
                value={str(form.values["center_disabled_label_key"]) || null}
                onChange={(v) => form.setFieldValue("center_disabled_label_key", v ?? "")}
              />
            </SimpleGrid>
          </Fieldset>

          <Fieldset legend="颜色">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                {colorField("ring_color", "外圈环颜色")}
                {colorField("sector_fallback_color", "扇区兜底背景色")}
                {colorField("center_button_color", "中心按钮底色（可抽）")}
                {colorField("center_button_disabled_color", "中心按钮底色（无次数）")}
                {colorField("center_text_color", "中心文字色（可抽）")}
                {colorField("center_text_disabled_color", "中心文字色（无次数）")}
              </SimpleGrid>
              <ColorInput
                label="扇区文字颜色（留空 = 按扇区底色自动黑/白）"
                format="rgba"
                value={str(form.values["sector_text_color"])}
                onChange={(v) => form.setFieldValue("sector_text_color", v === "" ? null : v)}
              />
            </Stack>
          </Fieldset>

          <Fieldset legend="扇区">
            <Stack gap="sm">
              <Text size="sm" fw={500}>
                扇区色板（按 displayOrder 循环取色，至少 3 色）
              </Text>
              {sectorColors.map((c, i) => (
                <Group key={i} gap="xs" wrap="nowrap">
                  <ColorInput
                    style={{ flex: 1 }}
                    value={c}
                    onChange={(v) => {
                      const next = [...sectorColors];
                      next[i] = v;
                      form.setFieldValue("sector_colors", next);
                    }}
                  />
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    disabled={sectorColors.length <= 3}
                    onClick={() =>
                      form.setFieldValue(
                        "sector_colors",
                        sectorColors.filter((_, idx) => idx !== i),
                      )
                    }
                  >
                    删除
                  </Button>
                </Group>
              ))}
              <Button
                size="xs"
                variant="light"
                onClick={() => form.setFieldValue("sector_colors", [...sectorColors, "#FF3B30"])}
              >
                添加颜色
              </Button>
            </Stack>
          </Fieldset>

          <Fieldset legend="音效">
            <Stack gap="sm">
              <Switch
                label="启用音效"
                checked={form.values["sound_enabled"] === true}
                onChange={(e) => form.setFieldValue("sound_enabled", e.currentTarget.checked)}
              />
              <SimpleGrid cols={2}>
                {uploadField("sound_spin_url", "旋转音效", "audio/*")}
                {uploadField("sound_win_url", "中奖音效", "audio/*")}
              </SimpleGrid>
            </Stack>
          </Fieldset>

          <Button type="submit" loading={patch.isPending}>
            保存并热加载
          </Button>
        </Stack>
      </form>
    </Card>
  );
}
